// Passage-selection contract (docs/plan/reading-selection-selector.md § "Passage
// (CRDB) and the TS read contract", § "Online query"): the request schema for
// the future POST /api/recommend-passages, the candidate shape it returns, the
// selector's constants, and the pure parts of the online query that decide
// which words count — the handoff and band assignment. No I/O: the route
// (Phase 7) loads the band set and runs the SQL.
//
// Keys (Open question 13, decided 2026-09-26): every word id here is a store
// `production_texts.vocab.word_id`, 1..4096, which is vocabulary index + 1
// once the store is renumbered to the model's order (#166). Because that
// equality is an assumption about someone else's table, every request carries
// the `vocabLayoutSha256` its ids were built under, and a layout other than
// the named manifest's is refused rather than mis-scored.

// Namespace import, not `import { z }` (./selection.ts).
import * as z from "zod";
import { SHA256_HEX_REGEX } from "../crypto";
import {
  CORPUS_VOCAB_SIZE,
  MAX_EXCLUDE_TEXT_IDS,
  publicationScopeSchema,
  vocabularyProfileShape,
  withParallelPredictions,
  wordIdsSchema,
} from "./selection";

// --- constants (§ "Fit threshold", § "Reference bands", Open questions 3, 5, 6)

/**
 * Nominal sight-vocabulary sizes of the reference bands, ascending. 2500 and
 * 3000 are the extended bands above the frequency-prefix ladder, built
 * prior-sampled only (fluency_model/bands.py EXTENDED_LABEL_SIZES).
 */
export const PASSAGE_BAND_SIZES = [
  25, 50, 100, 200, 400, 800, 1400, 2000, 2500, 3000,
] as const;

/**
 * θ_gen: the known-token share passages were generated at offline. The online
 * floor equals it, because nothing looser than it was ever generated.
 */
export const GENERATION_KNOWN_PERCENT = 90;
/** Online default for `minKnownPercent` (pitch-kms's 95%, Open question 5). */
export const DEFAULT_PASSAGE_MIN_KNOWN_PERCENT = 95;

/** Counted tokens (names excluded) below which a ratio is too lumpy to trust. */
export const MIN_PASSAGE_COUNTED_WORDS = 6;
/** Distinct name types a passage may carry exempt from the ratio. */
export const MAX_PASSAGE_NAME_TYPES = 3;

/** Calibrated P(known) at or above which a predicted word counts for a band. */
export const PREDICTED_KNOWN_MIN_P = 0.5;
/** Share of a band's known set a profile must cover to be in that band. */
export const BAND_COVERAGE_PERCENT = 90;

// --- model manifests

const sha256Schema = z.string().regex(SHA256_HEX_REGEX, "lowercase hex sha256");

/**
 * A manifest's handoff (packages/fluency-model `manifest.py` `Handoff`). A
 * `$pending` handoff is `pending` here and is never defaulted: every reader is
 * below it.
 */
export type ManifestHandoff =
  | {
      readonly kind: "settled";
      readonly minObservedKnown: number;
      readonly minObservedTotal: number;
    }
  | { readonly kind: "pending" };

/** What the online query reads from a model manifest, and nothing more. */
export interface ModelManifestRecord {
  readonly version: number;
  /** sha256 of the committed manifest file's bytes. */
  readonly sha256: string;
  /** The manifest's `vocabulary.layout_sha256`. */
  readonly vocabLayoutSha256: string;
  readonly handoff: ManifestHandoff;
  /**
   * Whether the calibration table is settled. A pending table leaves
   * `predictedConfidences` meaningless, so predicted words go unused.
   */
  readonly calibration: "settled" | "pending";
}

/**
 * The manifests a request may name: copies of
 * packages/fluency-model/manifests/*.json, pinned by file hash. passages.test.ts
 * re-derives every field from the committed file, so a republished manifest
 * that is not recorded here fails a test rather than a request.
 */
export const MODEL_MANIFESTS: readonly ModelManifestRecord[] = [
  {
    version: 1,
    sha256: "3f5ef1f258f981c84687727285daa86873912f94120d3e8f63039af111f23ef9",
    vocabLayoutSha256:
      "4fd1cfa37a2fa18c0679f0f7a50d5c2bf62ba075118692de8467aad802bfc000",
    handoff: { kind: "settled", minObservedKnown: 150, minObservedTotal: 150 },
    calibration: "settled",
  },
];

/** The recorded manifest a request names, or undefined for an unknown one. */
export function findModelManifest(named: {
  readonly version: number;
  readonly sha256: string;
}): ModelManifestRecord | undefined {
  return MODEL_MANIFESTS.find(
    (m) => m.version === named.version && m.sha256 === named.sha256,
  );
}

// --- passages

/** The `passages.passage` status CHECK, every value a stored row can hold. */
export const PassageStatus = z.enum([
  "accepted", // auto-accepted by routing
  "rejected", // auto-rejected
  "review", // waiting in the content-manager queue
  "human_accepted",
  "human_rejected",
  "superseded", // a later run re-judged the span; never live
]);
export type PassageStatus = z.infer<typeof PassageStatus>;

/** The statuses a request includes when it names none: the accepted ones. */
export const DEFAULT_PASSAGE_STATUSES = [
  "accepted",
  "human_accepted",
] as const satisfies readonly PassageStatus[];

/** One ranked passage. Bodiless, like `ReadingCandidate` (GET /api/passage/:id). */
export const PassageCandidate = z.object({
  id: z.uuid(),
  textId: z.uuid(),
  /** UTF-16 offsets into `text.text`, end exclusive. */
  charStart: z.number().int().nonnegative(),
  charEnd: z.number().int().positive(),
  wordCount: z.number().int().positive(),
  /** Per reader, computed at request time. */
  knownPercent: z.number().min(0).max(100),
  challengeScore: z.number(),
  standaloneScore: z.number().min(0).max(1),
  publication: z.object({ id: z.uuid(), title: z.string() }).nullable(),
});
export type PassageCandidate = z.infer<typeof PassageCandidate>;

// --- request

const knownPercentSchema = z.number().min(GENERATION_KNOWN_PERCENT).max(100);
// No ceiling: the pipeline caps no window's length (2026-09-28), and
// maxWords filters only when a caller sets it.
const passageWordsSchema = z.number().int().min(1);

export const passageOptionsSchema = z
  .object({
    /** Floored at θ_gen by the schema: nothing below it was generated. */
    minKnownPercent: knownPercentSchema.default(
      DEFAULT_PASSAGE_MIN_KNOWN_PERCENT,
    ),
    maxKnownPercent: knownPercentSchema.default(100),
    minWords: passageWordsSchema.default(1),
    maxWords: passageWordsSchema.optional(),
    /** Floor on Jev's stand-alone score; 0 filters nothing beyond status. */
    minStandaloneScore: z.number().min(0).max(1).default(0),
    statuses: z
      .array(PassageStatus)
      .min(1)
      .max(PassageStatus.options.length)
      .default([...DEFAULT_PASSAGE_STATUSES]),
    limit: z.number().int().min(1).max(100).default(20),
    excludeTextIds: z.array(z.uuid()).max(MAX_EXCLUDE_TEXT_IDS).default([]),
    publicationScope: publicationScopeSchema.optional(),
  })
  // Inverted windows would return an ordinary empty list (./selection.ts).
  .refine((o) => o.minKnownPercent <= o.maxKnownPercent, {
    message: "minKnownPercent must be less than or equal to maxKnownPercent",
    path: ["minKnownPercent"],
  })
  .refine((o) => o.maxWords === undefined || o.minWords <= o.maxWords, {
    message: "minWords must be less than or equal to maxWords",
    path: ["minWords"],
  });

export type PassageOptions = z.input<typeof passageOptionsSchema>;

/**
 * word_ids with no repeats. A repeated predicted id would leave the profile
 * holding whichever of its confidences came last, and count the other as an
 * ignored prediction; a repeated observed id would inflate the handoff's
 * counts. selection.ts's whole-text route keeps accepting repeats (its
 * schema is unchanged); this route refuses them.
 */
const distinctWordIdsSchema = wordIdsSchema.refine(
  (ids) => new Set(ids).size === ids.length,
  "word_ids must not repeat",
);

export const passageSelectionInputSchema = withParallelPredictions(
  z.object({
    ...vocabularyProfileShape,
    assessedWordIds: distinctWordIdsSchema,
    predictedWordIds: distinctWordIdsSchema,
    /**
     * word_ids the reader was assessed on and missed. They count toward the
     * handoff's `min_observed_total`, weigh 0, and cancel any prediction.
     */
    assessedUnknownWordIds: distinctWordIdsSchema,
    /** The manifest whose calibration table produced predictedConfidences. */
    modelManifest: z.strictObject({
      version: z.number().int().positive(),
      sha256: sha256Schema,
    }),
    /** The layout the request's word_ids were built under. */
    vocabLayoutSha256: sha256Schema,
    options: passageOptionsSchema.prefault({}),
  }),
)
  .refine((data) => findModelManifest(data.modelManifest) !== undefined, {
    message: "modelManifest is not a recorded manifest (version and sha256)",
    path: ["modelManifest"],
  })
  .refine(
    (data) =>
      findModelManifest(data.modelManifest)?.vocabLayoutSha256 ===
      data.vocabLayoutSha256,
    {
      message:
        "vocabLayoutSha256 is not the named manifest's vocabulary layout",
      path: ["vocabLayoutSha256"],
    },
  )
  // A word both known and missed is a caller bug with no right answer.
  .refine(
    (data) => {
      const missed = new Set(data.assessedUnknownWordIds);
      return !data.assessedWordIds.some((id) => missed.has(id));
    },
    {
      message:
        "a word_id is in both assessedWordIds and assessedUnknownWordIds",
      path: ["assessedUnknownWordIds"],
    },
  );

export type PassageSelectionInput = z.infer<typeof passageSelectionInputSchema>;
/** The wire shape a caller sends — options may be partial or absent. */
export type PassageSelectionRequest = z.input<
  typeof passageSelectionInputSchema
>;

// --- the handoff (§ "Online query" step 1)

/**
 * Below the handoff a reader's profile is exactly their observations. True
 * for a pending handoff (never defaulted), and when either count is short.
 * Counts are of distinct in-vocabulary words.
 */
export function isBelowHandoff(
  handoff: ManifestHandoff,
  observed: { readonly known: number; readonly total: number },
): boolean {
  switch (handoff.kind) {
    case "pending":
      return true;
    case "settled":
      return (
        observed.known < handoff.minObservedKnown ||
        observed.total < handoff.minObservedTotal
      );
  }
}

export interface ReaderProfile {
  readonly belowHandoff: boolean;
  readonly observedKnown: ReadonlySet<number>;
  readonly observedUnknown: ReadonlySet<number>;
  /**
   * word_id → calibrated P(known). Empty below the handoff or under a pending
   * calibration; never holds an observed word.
   */
  readonly predicted: ReadonlyMap<number, number>;
  /** Predictions sent but not used, for the route's `ignored` count. */
  readonly ignoredPredictions: number;
}

/**
 * Applies the manifest's handoff and calibration state to a request's
 * vocabulary arrays. Observations always win over predictions: a predicted
 * word the reader was assessed on (known or missed) is dropped.
 */
export function readerProfile(
  input: Pick<
    PassageSelectionInput,
    | "assessedWordIds"
    | "assessedUnknownWordIds"
    | "predictedWordIds"
    | "predictedConfidences"
  >,
  manifest: Pick<ModelManifestRecord, "handoff" | "calibration">,
): ReaderProfile {
  const observedKnown = new Set(input.assessedWordIds);
  const observedUnknown = new Set(input.assessedUnknownWordIds);
  const belowHandoff = isBelowHandoff(manifest.handoff, {
    known: observedKnown.size,
    total: observedKnown.size + observedUnknown.size,
  });
  const usable =
    !belowHandoff && manifest.calibration === "settled"
      ? input.predictedWordIds.flatMap((id, i) => {
          const p = input.predictedConfidences[i];
          return p === undefined ||
            observedKnown.has(id) ||
            observedUnknown.has(id)
            ? []
            : [[id, p] as const];
        })
      : [];
  const predicted = new Map(usable);
  return {
    belowHandoff,
    observedKnown,
    observedUnknown,
    predicted,
    ignoredPredictions: input.predictedWordIds.length - predicted.size,
  };
}

/**
 * The words that count as known for band assignment: observed-known, plus
 * predicted words at calibrated P ≥ 0.5 (present only above the handoff).
 */
export function bandKnownWordIds(profile: ReaderProfile): ReadonlySet<number> {
  const predictedKnown = [...profile.predicted]
    .filter(([, p]) => p >= PREDICTED_KNOWN_MIN_P)
    .map(([id]) => id);
  return new Set([...profile.observedKnown, ...predictedKnown]);
}

// --- reference bands (§ "Reference bands", § "Online query" step 2)

/**
 * A band's known set as a BIT(4096) literal: 4096 '0'/'1' characters, the
 * character at position k (0-based) being word_id k + 1. It is the shape
 * `text.common_word_bits` already has in CRDB, what pg returns for a
 * BIT(4096) column, and what corpus-db's `deriveKnownBits` and the corpus
 * pipeline's `build_vocab_bits` write — so the Phase 3 band builder emits
 * `"".join("1" if id in known else "0" for id in range(1, 4097))`, the
 * `reference_band` table stores it as BIT(4096), and the SQL prefilter can
 * `&` it with no conversion. A set with no word in it is refused: coverage
 * of nothing is not a band.
 */
export const knownBitsSchema = z
  .string()
  .regex(
    new RegExp(`^[01]{${CORPUS_VOCAB_SIZE}}$`),
    `${CORPUS_VOCAB_SIZE} '0'/'1' characters`,
  )
  .refine((bits) => bits.includes("1"), "a band knows at least one word");

export const bandConstructionSchema = z.enum([
  "frequency-prefix",
  "prior-sampled",
  "observed-real",
]);
export type BandConstruction = z.infer<typeof bandConstructionSchema>;

export const referenceBandSchema = z.object({
  bandId: z.string().min(1),
  labelSize: z.number().int().positive(),
  construction: bandConstructionSchema,
  belowHandoff: z.boolean(),
  knownBits: knownBitsSchema,
});
export type ReferenceBand = z.infer<typeof referenceBandSchema>;

const labelKey = (band: Pick<ReferenceBand, "labelSize" | "construction">) =>
  `${band.construction}:${band.labelSize}`;

/**
 * A band set file as packages/fluency-model's `make bands` writes it
 * (`manifests/fluency_prediction_4096_vN.bands.json`, format
 * `reference_bands/1`), read into camelCase. The file carries every
 * construction side by side; `servingBands` picks the ladder a route serves.
 * It keeps the manifest and vocabulary layout the bands were built under, so
 * a set can be refused for a manifest it was not built for. Refused: a
 * `known_count` that disagrees with its bits, and two bands of one
 * construction with the same label (which would make assignment depend on
 * input order).
 */
export const bandSetFileSchema = z
  .object({
    format: z.literal("reference_bands/1"),
    version: sha256Schema,
    manifest_sha256: sha256Schema,
    inputs: z.object({ vocab_layout_sha256: sha256Schema }),
    bands: z.array(
      z
        .object({
          band_id: z.string().min(1),
          label_size: z.number().int().positive(),
          construction: bandConstructionSchema,
          below_handoff: z.boolean(),
          known_count: z.number().int().positive(),
          known_bits: knownBitsSchema,
        })
        .refine(
          (b) => b.known_bits.split("1").length - 1 === b.known_count,
          "known_count must equal the number of '1' bits",
        ),
    ),
  })
  .transform((file) => ({
    version: file.version,
    manifestSha256: file.manifest_sha256,
    vocabLayoutSha256: file.inputs.vocab_layout_sha256,
    bands: file.bands.map((b): ReferenceBand => ({
      bandId: b.band_id,
      labelSize: b.label_size,
      construction: b.construction,
      belowHandoff: b.below_handoff,
      knownBits: b.known_bits,
    })),
  }))
  .refine(
    (set) => new Set(set.bands.map(labelKey)).size === set.bands.length,
    "two bands share a construction and a label",
  );
export type BandSet = z.output<typeof bandSetFileSchema>;

// Not exported: only servingBands can build a ServingBands.
const servingBrand: unique symbol = Symbol("ServingBands");

/**
 * The ladders a route serves, each one band per label: the only input
 * `assignBand` takes, so assignment can never weigh two bands of one label
 * against each other (a prior-sampled band is far smaller than its label,
 * and a reader who knows its 59 words is not a 400-word reader) or depend on
 * input order. Bound to the manifest its bands were built for: the route
 * compares `manifestSha256` and `vocabLayoutSha256` with the request's.
 */
export interface ServingBands {
  /** The construction served where it exists; frequency-prefix elsewhere. */
  readonly construction: BandConstruction;
  readonly manifestSha256: string;
  readonly vocabLayoutSha256: string;
  /** The ladder for a reader at or above the handoff. Ascending by label. */
  readonly bands: readonly ReferenceBand[];
  /**
   * Every frequency-prefix band, ascending: the ladder for a reader below
   * the handoff, who is never placed by a prior-sampled band (§ "Reference
   * bands", item 1). It is `bands` up to the largest frequency-prefix label,
   * one frequency-prefix band at each; the labels above it are served to a
   * reader at or above the handoff only.
   */
  readonly prefixBands: readonly ReferenceBand[];
  readonly [servingBrand]: true;
}

export type ServingBandsResult =
  | { readonly ok: true; readonly serving: ServingBands }
  | { readonly ok: false; readonly error: string };

/**
 * The serving ladder of a band set (a parsed file, or `reference_band` rows
 * with their `band_set` header) for `manifest`, by the handoff rule of
 * § "Reference bands": at each label the `construction` band, falling back to
 * the frequency-prefix one. Below the handoff only frequency-prefix bands are
 * built, so asking for `prior-sampled` serves the mixed ladder — prefix below
 * the handoff, prior-sampled at and above it — and a prior-sampled band marked
 * below the handoff is refused.
 *
 * Every label up to the largest frequency-prefix one must have a
 * frequency-prefix band: those are `prefixBands`, the only ladder a reader
 * below the handoff is placed by. A label above them (v1's extended 2,500
 * and 3,000 bands) has no frequency-prefix band to fall back to, so its only
 * band, which must be prior-sampled at or above the handoff, joins every
 * ladder, and never `prefixBands`. The pipeline's `selectBands`
 * (packages/corpus `passages/fit.ts`) is the same rule;
 * `passages/goldens/v1_serving_ladders.json` pins both to it.
 *
 * Refused: a set built for another manifest or vocabulary layout, an empty
 * set or one with no frequency-prefix band, two bands of one construction at
 * one label, a label under the largest frequency-prefix one with no
 * frequency-prefix band (a reader below the handoff would have no band to be
 * placed by there), and a label above it with more than one band or with
 * one that is not prior-sampled at or above the handoff.
 */
export function servingBands(
  set: Pick<BandSet, "manifestSha256" | "vocabLayoutSha256" | "bands">,
  manifest: Pick<ModelManifestRecord, "sha256" | "vocabLayoutSha256">,
  construction: BandConstruction,
): ServingBandsResult {
  const refuse = (error: string): ServingBandsResult => ({ ok: false, error });
  if (set.manifestSha256 !== manifest.sha256) {
    return refuse("the band set was built for another manifest");
  }
  if (set.vocabLayoutSha256 !== manifest.vocabLayoutSha256) {
    return refuse("the band set was built under another vocabulary layout");
  }
  if (set.bands.length === 0) return refuse("no band in the set");
  const keys = set.bands.map(labelKey);
  const repeated = set.bands.find((b, i) => keys.indexOf(labelKey(b)) !== i);
  if (repeated !== undefined) {
    return refuse(
      `two ${repeated.construction} bands have label ${repeated.labelSize}`,
    );
  }
  const ascending = [...set.bands].sort((a, b) => a.labelSize - b.labelSize);
  const prefixBands = ascending.filter(
    (b) => b.construction === "frequency-prefix",
  );
  const top = prefixBands[prefixBands.length - 1];
  if (top === undefined) return refuse("no frequency-prefix band in the set");
  // A band, or why the label cannot be served.
  const pick = (label: number): ReferenceBand | string => {
    const options = ascending.filter((b) => b.labelSize === label);
    const prefix = options.find((b) => b.construction === "frequency-prefix");
    if (prefix !== undefined) {
      return options.find((b) => b.construction === construction) ?? prefix;
    }
    if (label < top.labelSize) {
      return `no frequency-prefix band at label ${label}`;
    }
    const [only, ...others] = options;
    if (only === undefined || others.length > 0) {
      return `more than one band at label ${label}, which has no frequency-prefix band`;
    }
    // What the extended bands are; anything else is a construction the rule
    // has no place for (a lone observed-real band is not a ladder).
    return only.construction === "prior-sampled" && !only.belowHandoff
      ? only
      : `${only.bandId} has no frequency-prefix band beside it and is not prior-sampled at or above the handoff`;
  };
  const picked = [...new Set(ascending.map((b) => b.labelSize))].map(pick);
  const unserved = picked.find((p): p is string => typeof p === "string");
  if (unserved !== undefined) return refuse(unserved);
  const bands = picked.filter((p): p is ReferenceBand => typeof p !== "string");
  const priorBelow = bands.find(
    (b) => b.belowHandoff && b.construction === "prior-sampled",
  );
  if (priorBelow !== undefined) {
    return refuse(`${priorBelow.bandId} is prior-sampled below the handoff`);
  }
  return {
    ok: true,
    serving: {
      construction,
      manifestSha256: set.manifestSha256,
      vocabLayoutSha256: set.vocabLayoutSha256,
      bands,
      prefixBands,
      [servingBrand]: true,
    },
  };
}

/**
 * The share of a band's known set the reader covers, as the integer test
 * `100·covered ≥ 90·size` — no float decides membership.
 */
function covers(bits: string, known: ReadonlySet<number>): boolean {
  // knownBitsSchema admits only '0'/'1', so a per-character split is exact.
  const ids = bits.split("").flatMap((bit, k) => (bit === "1" ? [k + 1] : []));
  const covered = ids.filter((id) => known.has(id)).length;
  return 100 * covered >= BAND_COVERAGE_PERCENT * ids.length;
}

/**
 * The largest band (by `labelSize`) whose known set the reader covers at
 * ≥ 90%, or undefined when none is covered. A reader below the handoff
 * (`ReaderProfile.belowHandoff`, from `isBelowHandoff`) is placed only by
 * the frequency-prefix ladder: v1's b400-prior is 59 words, the top 59, so
 * the mixed ladder would put a reader who knows just those at 400. Every
 * band is tested, so a non-nested set cannot hide a larger covered band
 * behind a smaller uncovered one; labels are unique (`servingBands`), so no
 * tie depends on order.
 */
export function assignBand(
  serving: ServingBands,
  reader: {
    readonly belowHandoff: boolean;
    /** `bandKnownWordIds` of the reader's profile. */
    readonly known: ReadonlySet<number>;
  },
): ReferenceBand | undefined {
  const { belowHandoff, known } = reader;
  return (belowHandoff ? serving.prefixBands : serving.bands)
    .filter((band) => covers(band.knownBits, known))
    .reduce<ReferenceBand | undefined>(
      (best, band) =>
        best === undefined || band.labelSize > best.labelSize ? band : best,
      undefined,
    );
}
